import type { ResourceType } from "@/modules/api/resourceTypes.tsx";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import type { ResourceId } from "@/modules/network/genericResources.ts";
import type { IpAddressWithType } from "@/modules/network/ipAddress/ipAddressApi.ts";
import type { LoadBalancerWithType } from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import { getResourceName, type AnyResource } from "@/modules/resource/getResourceName.ts";
import type { ManagedServiceLoc } from "@/modules/services/servicesQuery.ts";
import type { IpAddressAssignBody, IpAddressResourceType } from "@warrenio/api-spec/spec.oats.gen";
import { unreachableSwitchCase } from "@warrenio/utils/unreachable";

export type IpAssignableResource = VirtualMachineLoc | LoadBalancerWithType | ManagedServiceLoc;

export function getAssignBodyFromResource(resource: IpAssignableResource): IpAddressAssignBody {
    switch (resource.$type) {
        case "managed_service":
            return { assigned_to_resource_type: "service", assigned_to: resource.uuid };
        case "load_balancer":
            return { assigned_to_resource_type: "load_balancer", assigned_to: resource.uuid };
        case "virtual_machine":
            return { assigned_to_resource_type: "virtual_machine", assigned_to: resource.uuid };
    }
}

export function getAddressFromResource(resource: IpAssignableResource | null): string | undefined {
    if (resource !== null) {
        switch (resource.$type) {
            case "managed_service":
                return "service_ip" in resource.properties ? resource.properties.service_ip : undefined;
            case "virtual_machine":
                return resource.private_ipv4;
            case "load_balancer":
                return resource.private_address;
        }
    }
    return undefined;
}

/** An ID for a resource that can be assigned with an IP address */
export interface IpResourceId extends ResourceId<IpAddressResourceType> {
    $type: IpAddressResourceType;
    $id: string;
    location: string;
}

export function getResourceId(item: IpAssignableResource): IpResourceId {
    return {
        $type: item.$type === "managed_service" ? "service" : item.$type,
        $id: item.uuid,
        location: item.location,
    };
}

export const UNASSIGNED_RESOURCE = null;
export type UNASSIGNED_RESOURCE = typeof UNASSIGNED_RESOURCE;

export const UNASSIGNED_KEY = "__unassigned__";

export function getResourceStringId(item: IpAssignableResource | UNASSIGNED_RESOURCE): string {
    if (item === UNASSIGNED_RESOURCE) {
        return UNASSIGNED_KEY;
    }

    return resourceIdToString(getResourceId(item));
}

export function resourceIdToString(id: IpResourceId | UNASSIGNED_RESOURCE): string {
    if (id === UNASSIGNED_RESOURCE) {
        return UNASSIGNED_KEY;
    }

    return JSON.stringify(id);
}

export function getDisplayName(item: AnyResource | UNASSIGNED_RESOURCE): string {
    if (item === UNASSIGNED_RESOURCE) {
        return "Unassigned";
    }

    return getResourceName(item);
}

export function getAssignedResourceId({
    assigned_to,
    assigned_to_resource_type,
    location,
}: IpAddressWithType): IpResourceId | UNASSIGNED_RESOURCE {
    if (assigned_to == null || assigned_to_resource_type == null) {
        return UNASSIGNED_RESOURCE;
    }
    return {
        $type: assigned_to_resource_type,
        $id: assigned_to,
        location,
    };
}

export function ipAddressResourceTypeToType(type: IpAddressResourceType) {
    switch (type) {
        case "load_balancer":
            return "load_balancer" satisfies ResourceType;
        case "service":
            return "managed_service" satisfies ResourceType;
        case "virtual_machine":
            return "virtual_machine" satisfies ResourceType;
        default:
            unreachableSwitchCase(type);
    }
}
